/**
 * Accounts API (`/api/v1/accounts/`) error `code` values emitted by the backend handlers
 * (see `backend/apps/accounts`; the codes are the contract).
 */
export const ACCOUNTS_API_ERROR_CODES = [
  'ACCOUNT_DISABLED',
  'ACCOUNT_NOT_FOUND',
  'ACCOUNT_PENDING_VERIFICATION',
  'ACCESS_DENIED',
  'AUTHENTICATION_FAILED',
  'AUTH_REQUIRED',
  'AUTHORIZATION_CODE_REQUIRED',
  'EMAIL_DISPATCH_FAILED',
  'INSUFFICIENT_PERMISSIONS',
  'INTERNAL_ERROR',
  'INVALID_CREDENTIALS',
  'INVALID_REQUEST',
  'INVITATION_CREATION_FAILED',
  'INVITATION_EMAIL_FAILED',
  'INVITATION_EMAIL_MISMATCH',
  'INVITATION_ERROR',
  'INVITATION_EXPIRED',
  'INVITATION_INVALID',
  'INVITATION_NOT_FOUND',
  'INVITATION_REQUIRED',
  'INVITATION_RESEND_FAILED',
  'INVITATION_REVOKED',
  'LOGOUT_FAILED',
  'OAUTH_CONFIGURATION_ERROR',
  'OAUTH_DOMAIN_NOT_ALLOWED',
  'OAUTH_ERROR',
  'OAUTH_PROCESSING_FAILED',
  'PASSWORD_CHANGE_FAILED',
  'PASSWORD_RESET_FAILED',
  'PASSWORD_RESET_REQUEST_FAILED',
  'PERMISSION_DENIED',
  'PROFILE_UPDATE_FAILED',
  'RATE_LIMITED',
  'REGISTRATION_FAILED',
  'ROLE_ASSIGNMENT_FAILED',
  'ROLE_CHANGE_FAILED',
  'ROLE_ELEVATION_FAILED',
  'ROLE_REMOVAL_FAILED',
  'SCHOOL_ASSIGNMENT_FAILED',
  'SUPER_ADMIN_PROTECTED',
  'TOKEN_EXCHANGE_FAILED',
  'TOKEN_NOT_VALID',
  'TOKEN_PARSE_ERROR',
  'TOKEN_REFRESH_FAILED',
  'TOKEN_VALIDATION_FAILED',
  'USER_NOT_FOUND',
  'USER_SERVICE_ERROR',
  'USER_UPDATE_FAILED',
  'VALIDATION_ERROR',
  'VERIFICATION_FAILED',
] as const

export type AccountsApiErrorCode = (typeof ACCOUNTS_API_ERROR_CODES)[number]

const accountsApiErrorCodeSet = new Set<string>(ACCOUNTS_API_ERROR_CODES)

export function isAccountsApiErrorCode(code: string | undefined): code is AccountsApiErrorCode {
  return code !== undefined && accountsApiErrorCodeSet.has(code)
}

/**
 * Codes the backend or older clients may still return; accounts UI forms branch on these.
 * Suppress global API toasts so screens can show their own copy.
 */
export const ACCOUNTS_LEGACY_SUPPRESS_TOAST_CODES = [
  'AUTHENTICATION_ERROR',
  'CURRENT_PASSWORD_INVALID',
  'EMAIL_EXISTS',
  'INVITATION_ALREADY_SENT',
  'PASSWORD_INVALID',
  'PASSWORD_MISMATCH',
  'ROLE_NOT_ASSIGNABLE',
  'TOKEN_INVALID',
  'VALIDATION_FAILED',
] as const

const authSuppressToastSet = new Set<string>([
  ...ACCOUNTS_API_ERROR_CODES,
  ...ACCOUNTS_LEGACY_SUPPRESS_TOAST_CODES,
])

/** Union of {@link ACCOUNTS_API_ERROR_CODES} and {@link ACCOUNTS_LEGACY_SUPPRESS_TOAST_CODES} for axios (no duplicate toasts on auth flows). */
export function isAuthSuppressToastCode(code: string | undefined): boolean {
  return code !== undefined && authSuppressToastSet.has(code)
}
